import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD } from '../legacy-ball/table-management/designTokens';

interface HowToDoc {
  id: string;
  title: string;
  description: string;
  url: string;
  createdAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function HowToDocumentsModal({ open, onClose }: Props) {
  const { user } = useAuth();
  const canManage = user?.role === 'Organizer' || user?.role === 'Admin';

  const [docs, setDocs] = useState<HowToDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [addError, setAddError] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (open) loadDocs();
  }, [open]);

  async function loadDocs() {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<HowToDoc[]>('/how-to-docs');
      setDocs(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!newTitle.trim() || !newUrl.trim()) {
      setAddError('Title and URL are required');
      return;
    }
    setAddError('');
    try {
      const doc = await apiClient<HowToDoc>('/how-to-docs', {
        method: 'POST',
        body: { title: newTitle.trim(), description: newDescription.trim(), url: newUrl.trim() },
      });
      setDocs((prev) => [...prev, doc].sort((a, b) => a.title.localeCompare(b.title)));
      setNewTitle('');
      setNewDescription('');
      setNewUrl('');
      setShowAddForm(false);
    } catch (err: any) {
      setAddError(err?.message || 'Failed to add document');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this document from the library?')) return;
    setDeleting(id);
    try {
      await apiClient(`/how-to-docs/${id}`, { method: 'DELETE' });
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch (err: any) {
      setError(err?.message || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  }

  function handleOpen(doc: HowToDoc) {
    // Open via the API's public view endpoint (no auth required, serves HTML directly)
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    window.open(`${apiBase}/how-to-docs/${doc.id}/view`, '_blank', 'noopener');
  }

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>KDUHUB How To Documents</h2>
            <p style={styles.subtitle}>{docs.length} document{docs.length !== 1 ? 's' : ''} available</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.loadingState}>Loading documents...</div>
        ) : (
          <>
            {/* Document list */}
            <div style={styles.docList}>
              {docs.length === 0 && (
                <div style={styles.emptyState}>No documents yet. Add one below.</div>
              )}
              {docs.map((doc) => (
                <div key={doc.id} style={styles.docCard}>
                  <div style={styles.docInfo}>
                    <div style={styles.docTitle}>{doc.title}</div>
                    <div style={styles.docDesc}>{doc.description}</div>
                  </div>
                  <div style={styles.docActions}>
                    <button
                      style={styles.printBtn}
                      onClick={() => handleOpen(doc)}
                      aria-label={`Open ${doc.title} for printing`}
                    >
                      🖨️ Open
                    </button>
                    {canManage && (
                      <button
                        style={styles.removeBtn}
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleting === doc.id}
                        aria-label={`Remove ${doc.title}`}
                      >
                        {deleting === doc.id ? '...' : '🗑️'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add form */}
            {canManage && (
              <div style={styles.addSection}>
                {!showAddForm ? (
                  <button style={styles.addBtn} onClick={() => setShowAddForm(true)}>
                    + Add Document
                  </button>
                ) : (
                  <div style={styles.addForm}>
                    <div style={styles.formTitle}>Add New Document</div>
                    <input
                      style={styles.input}
                      placeholder="Document title *"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                    />
                    <input
                      style={styles.input}
                      placeholder="Short description"
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                    />
                    <input
                      style={styles.input}
                      placeholder="URL (full URL to the document) *"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                    />
                    {addError && <div style={styles.addError}>{addError}</div>}
                    <div style={styles.formActions}>
                      <button style={styles.saveBtn} onClick={handleAdd}>Save</button>
                      <button style={styles.cancelBtn} onClick={() => { setShowAddForm(false); setAddError(''); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: BG_CARD, borderRadius: '12px',
    width: '95%', maxWidth: '700px', maxHeight: '85vh',
    overflow: 'auto', padding: '1.5rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '1.25rem',
  },
  title: { margin: 0, fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK },
  subtitle: { margin: '4px 0 0', fontSize: '0.8rem', color: TEXT_MUTED },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '1.2rem',
    cursor: 'pointer', padding: '4px 8px', color: TEXT_MUTED, lineHeight: 1,
  },
  error: {
    color: '#d32f2f', backgroundColor: '#ffebee', padding: '0.5rem 0.75rem',
    borderRadius: '6px', fontSize: '0.82rem', marginBottom: '0.75rem',
  },
  loadingState: { textAlign: 'center', padding: '2rem', color: TEXT_MUTED, fontSize: '0.9rem' },
  emptyState: { textAlign: 'center', padding: '1.5rem', color: TEXT_MUTED, fontSize: '0.85rem', fontStyle: 'italic' },
  docList: {
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },
  docCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.75rem 1rem',
    border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px',
    backgroundColor: '#fafbfc',
    gap: '0.75rem',
  },
  docInfo: { flex: 1, minWidth: 0 },
  docTitle: { fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK },
  docDesc: { fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '2px' },
  docActions: { display: 'flex', gap: '0.4rem', flexShrink: 0 },
  printBtn: {
    padding: '0.35rem 0.7rem', backgroundColor: PRIMARY, color: '#fff',
    border: 'none', borderRadius: '5px', fontSize: '0.78rem', fontWeight: 500,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  removeBtn: {
    padding: '0.3rem 0.5rem', background: 'none', border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer',
  },
  addSection: { marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${BORDER_LIGHT}` },
  addBtn: {
    padding: '0.5rem 1rem', backgroundColor: PRIMARY, color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 500,
    cursor: 'pointer',
  },
  addForm: {
    backgroundColor: '#f9fafb', border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px', padding: '1rem',
  },
  formTitle: { fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.75rem' },
  input: {
    width: '100%', padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`, borderRadius: '5px',
    fontSize: '0.85rem', marginBottom: '0.5rem', boxSizing: 'border-box' as const,
  },
  addError: { color: '#d32f2f', fontSize: '0.78rem', marginBottom: '0.5rem' },
  formActions: { display: 'flex', gap: '0.5rem', marginTop: '0.25rem' },
  saveBtn: {
    padding: '0.4rem 1rem', backgroundColor: PRIMARY, color: '#fff',
    border: 'none', borderRadius: '5px', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer',
  },
  cancelBtn: {
    padding: '0.4rem 1rem', backgroundColor: '#e0e0e0', color: '#333',
    border: 'none', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer',
  },
};
